package com.nonopn.skywatcher.common.app.pages.administrators

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.widthIn
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.rounded.Add
import androidx.compose.material.icons.rounded.Email
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.nonopn.icons.ScanQr
import com.nonopn.skywatcher.common.app.home.LocalThunderDetectionModel
import com.nonopn.skywatcher.common.app.navigation.RouterAdministratorsScanQr
import com.nonopn.skywatcher.common.app.navigation.RouterUsersScanQr
import com.nonopn.skywatcher.common.app.pages.sentinels.ThunderDetectionWithMetadata
import com.nonopn.skywatcher.design.appbar.FloatingActionButtonState
import com.nonopn.skywatcher.design.local.LocalConfirmPopup
import com.nonopn.skywatcher.design.utils.localized
import com.nonopn.skywatcher.design.widgets.PromptDialog
import com.nonopn.skywatcher.design.widgets.abslists.AbstractLists
import com.nonopn.skywatcher.design.widgets.abslists.ListModelState
import eu.codlab.compose.widgets.TextNormal
import eu.codlab.platform.Platform
import eu.codlab.platform.currentPlatform
import eu.codlab.viewmodel.rememberViewModel


@Composable
fun AdministratorsContent(
    modifier: Modifier,
    thunderDetection: ThunderDetectionWithMetadata,
) = GeneralContent(
    modifier,
    thunderDetection,
    AdministratorLocalHolder,
    UserMode.Admin
)

@Composable
fun UsersContent(
    modifier: Modifier,
    thunderDetection: ThunderDetectionWithMetadata,
) = GeneralContent(
    modifier,
    thunderDetection,
    UserLocalHolder,
    UserMode.User
)

@Composable
private fun GeneralContent(
    modifier: Modifier,
    thunderDetection: ThunderDetectionWithMetadata,
    locales: LocaleHolder,
    mode: UserMode
) {
    val localThunderDetectionModel = LocalThunderDetectionModel.current
    val localThunderDetectionState by localThunderDetectionModel.states.collectAsState()
    var showPrompt by remember { mutableStateOf(false) }
    var showRemovePrompt by remember { mutableStateOf(false) }

    val model = rememberViewModel {
        AdministratorsModel(
            thunderDetection,
            usersOrAdminMode = mode,
            currentApi = localThunderDetectionState.currentApi!!
        )
    }
    val state by model.states.collectAsState()
    val popup = LocalConfirmPopup.current

    val titleError = locales.add_new_user_couldnt_invite_title.localized()
    val textError = locales.add_new_user_couldnt_invite_text.localized()
    val textRemoveError = locales.remove_user_couldnt_remove_text.localized()

    if (showPrompt && state.canInvite) {
        PromptDialog(
            locales.add_new_user_title.localized(),
            locales.add_new_user_text.localized(),
            onDismiss = {
                showPrompt = false
            },
            onConfirm = {
                model.inviteToSentinel(
                    it,
                    onSuccess = { /* nothing */ },
                    onError = {
                        popup.show(
                            titleError,
                            textError,
                        )
                    }
                )

                showPrompt = false
            }
        )
    }

    val removeTitle = locales.remove_user_title.localized()
    val removeText = locales.remove_user_text.localized()

    DisposableEffect(state.canInvite) {
        if (state.canInvite) {
            val isMobile = when (currentPlatform) {
                Platform.ANDROID -> true
                Platform.IOS -> true
                Platform.JVM -> false
                Platform.JS -> false
                Platform.LINUX -> false
                Platform.MACOS -> false
                Platform.WINDOWS -> false
            }

            localThunderDetectionModel.setFloatingActionButton(
                if (isMobile) {
                    FloatingActionButtonState.Menu(
                        icon = Icons.Rounded.Add,
                        contentDescription = "",
                        items = listOf(
                            FloatingActionButtonState.Button(
                                icon = Icons.Rounded.Email,
                                contentDescription = "",
                                label = "",
                                action = {
                                    showPrompt = true
                                }
                            ),
                            FloatingActionButtonState.Button(
                                icon = Icons.Rounded.ScanQr,
                                contentDescription = "",
                                label = "",
                                action = {
                                    val route = when (mode) {
                                        UserMode.User -> RouterUsersScanQr
                                        UserMode.Admin -> RouterAdministratorsScanQr
                                    }

                                    localThunderDetectionModel.show(
                                        route.navigateTo(
                                            localThunderDetectionState.selectedThunderDetection.detection.uuid
                                        )
                                    )
                                }
                            )
                        )
                    )
                } else {
                    FloatingActionButtonState.Button(
                        icon = Icons.Rounded.Email,
                        contentDescription = "",
                        label = "",
                        action = {
                            showPrompt = true
                        }
                    )
                }
            )
        } else {
            localThunderDetectionModel.setFloatingActionButton(null)
        }

        onDispose {
            // nothing
        }
    }

    LaunchedEffect(Unit) {
        model.load()
    }

    val errorText = when {
        state.modelState.state == ListModelState.LOADED && !state.canInvite -> locales.cant_invite.localized()
        state.modelState.state == ListModelState.ERROR -> locales.couldnt_load.localized()
        else -> null
    }

    if (null != errorText) {
        Column(
            modifier = modifier,
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center,
        ) {
            TextNormal(
                modifier = Modifier.widthIn(200.dp, 400.dp),
                text = errorText
            )
        }
        return
    }

    AbstractLists(model = model) { _, administrator ->
        RenderUserRow(
            user = administrator,
            canInvite = state.canInvite,
            onLongClick = {
                popup.show(
                    title = removeTitle,
                    text = removeText,
                    onDismiss = {
                        showPrompt = false
                    },
                    onConfirm = {
                        model.removeFromSentinel(
                            it,
                            onSuccess = { /* nothing */ },
                            onError = {
                                popup.show(
                                    titleError,
                                    textRemoveError,
                                )
                            }
                        )

                        showPrompt = false
                    }
                )
            }
        )
    }
}